import { useCallback, useEffect, useRef, useState } from 'react'

const useSubmitLock = () => {
  const lockRef = useRef(false)
  const mountedRef = useRef(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const unlock = useCallback(() => {
    lockRef.current = false
    if (mountedRef.current) {
      setIsSubmitting(false)
    }
  }, [])

  const wrapClick = useCallback((handler?: (event?: any) => unknown) => {
    return async (event?: any) => {
      if (lockRef.current) {
        event?.preventDefault?.()
        event?.stopPropagation?.()
        return
      }

      lockRef.current = true
      setIsSubmitting(true)

      try {
        await handler?.(event)
      } finally {
        unlock()
      }
    }
  }, [unlock])

  const wrapFormSubmit = useCallback(
    (
      handleSubmit: (
        onValid: (data: any) => unknown,
        onInvalid?: () => void
      ) => (event?: any) => unknown,
      onValid: (data: any) => unknown
    ) => {
      return (event?: unknown) => {
        const nativeEvent = event as {
          preventDefault?: () => void
          stopPropagation?: () => void
        }

        if (lockRef.current) {
          nativeEvent?.preventDefault?.()
          nativeEvent?.stopPropagation?.()
          return
        }

        lockRef.current = true
        setIsSubmitting(true)

        return handleSubmit(
          async (data) => {
            try {
              await onValid(data)
            } finally {
              unlock()
            }
          },
          () => {
            unlock()
          }
        )(event)
      }
    },
    [unlock]
  )

  return { isSubmitting, wrapClick, wrapFormSubmit }
}

export default useSubmitLock
